import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { toast } from "sonner";
import { Plus, Trash2, Edit, Music2, Users, ChevronUp, ChevronDown, Save, AlertTriangle, ChevronRight, User, Copy, UserPlus, ListTree, RotateCcw } from "lucide-react";

const Sections = () => {
  const { user } = useAuth();
  const [sections, setSections] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    order: 0
  });

  // Bulk-add-players-to-section state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSection, setBulkSection] = useState(null);
  const [bulkRows, setBulkRows] = useState([
    { name: "", email: "", phone: "" },
    { name: "", email: "", phone: "" },
    { name: "", email: "", phone: "" },
  ]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Parts management state
  const [partsDialogOpen, setPartsDialogOpen] = useState(false);
  const [partsSection, setPartsSection] = useState(null);
  const [partsDraft, setPartsDraft] = useState([]);
  const [partsSaving, setPartsSaving] = useState(false);

  // Reset-to-default state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sectionsRes, playersRes] = await Promise.all([
        apiClient.get("/sections"),
        apiClient.get("/players")
      ]);
      // Sort sections by order
      const sortedSections = sectionsRes.data.sort((a, b) => a.order - b.order);
      setSections(sortedSections);
      setPlayers(playersRes.data);
      setHasOrderChanges(false);
    } catch (error) {
      toast.error("Failed to load sections");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSection) {
        await apiClient.put(`/sections/${editingSection.id}`, formData);
        toast.success("Section updated");
      } else {
        await apiClient.post("/sections", { ...formData, order: sections.length });
        toast.success("Section created");
      }
      setDialogOpen(false);
      setEditingSection(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save section");
    }
  };

  const handleDelete = async () => {
    if (!sectionToDelete) return;
    
    try {
      await apiClient.delete(`/sections/${sectionToDelete.id}`);
      toast.success("Section deleted");
      setDeleteDialogOpen(false);
      setSectionToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete section");
    }
  };

  const confirmDelete = (section) => {
    setSectionToDelete(section);
    setDeleteDialogOpen(true);
  };

  const handleClone = async (section) => {
    try {
      const res = await apiClient.post(`/sections/${section.id}/clone`);
      toast.success(`Cloned as "${res.data.name}"`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clone section");
    }
  };

  const openBulkAdd = (section) => {
    setBulkSection(section);
    setBulkRows([
      { name: "", email: "", phone: "" },
      { name: "", email: "", phone: "" },
      { name: "", email: "", phone: "" },
    ]);
    setBulkDialogOpen(true);
  };

  const updateBulkRow = (idx, field, value) => {
    setBulkRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addBulkRow = () => {
    setBulkRows((rows) => [...rows, { name: "", email: "", phone: "" }]);
  };

  const removeBulkRow = (idx) => {
    setBulkRows((rows) => rows.filter((_, i) => i !== idx));
  };

  const submitBulkAdd = async () => {
    const payload = bulkRows
      .map((r) => ({ name: r.name.trim(), email: r.email.trim(), phone: r.phone.trim() || null }))
      .filter((r) => r.name && r.email);
    if (payload.length === 0) {
      toast.error("Please fill in at least one player's name and email");
      return;
    }
    setBulkSubmitting(true);
    try {
      const res = await apiClient.post(
        `/sections/${bulkSection.id}/bulk-add-players`,
        { players: payload }
      );
      const { created_count, skipped_count, skipped } = res.data;
      if (created_count > 0) {
        toast.success(
          `Added ${created_count} player${created_count === 1 ? "" : "s"} to ${bulkSection.name}${
            skipped_count > 0 ? ` (${skipped_count} skipped — already in band)` : ""
          }`
        );
      } else {
        toast.error(
          skipped.length > 0
            ? `Nothing added — ${skipped_count} already in the band`
            : "Nothing added"
        );
      }
      setBulkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add players");
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setFormData({
      name: section.name,
      order: section.order
    });
    setDialogOpen(true);
  };

  // Parts management
  const openPartsDialog = (section) => {
    setPartsSection(section);
    setPartsDraft((section.parts || []).map((p, i) => ({ ...p, order: p.order ?? i })));
    setPartsDialogOpen(true);
  };

  const savePartsList = async () => {
    if (!partsSection) return;
    setPartsSaving(true);
    try {
      const cleaned = partsDraft
        .map((p, i) => ({ ...p, name: (p.name || "").trim(), order: i }))
        .filter((p) => p.name);
      await apiClient.put(`/sections/${partsSection.id}/parts`, { parts: cleaned });
      toast.success(`Parts saved for ${partsSection.name}`);
      setPartsDialogOpen(false);
      setPartsSection(null);
      setPartsDraft([]);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save parts");
    } finally {
      setPartsSaving(false);
    }
  };

  const addPartRow = () => {
    setPartsDraft((prev) => [...prev, { id: undefined, name: "", order: prev.length }]);
  };

  const updatePartRow = (idx, name) => {
    setPartsDraft((prev) => prev.map((p, i) => (i === idx ? { ...p, name } : p)));
  };

  const removePartRow = (idx) => {
    setPartsDraft((prev) => prev.filter((_, i) => i !== idx));
  };

  const movePartRow = (idx, dir) => {
    setPartsDraft((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  // Reset to default
  const performReset = async () => {
    setResetting(true);
    try {
      const res = await apiClient.post("/band/reset-sections-to-default");
      toast.success(`Reset complete — ${res.data.created} default sections created (${res.data.deleted} removed)`);
      setResetDialogOpen(false);
      setResetConfirm("");
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      order: sections.length
    });
  };

  const getPlayersInSection = useCallback((sectionId) => {
    return players.filter(p => p.section_ids?.includes(sectionId) || p.section_id === sectionId);
  }, [players]);

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    
    // Swap sections
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    // Update order values
    newSections.forEach((section, i) => {
      section.order = i;
    });
    
    setSections(newSections);
    setHasOrderChanges(true);
  };

  const saveOrder = async () => {
    try {
      const sectionIds = sections.map(s => s.id);
      await apiClient.put("/sections/reorder", { section_ids: sectionIds });
      toast.success("Section order saved");
      setHasOrderChanges(false);
    } catch (error) {
      toast.error("Failed to save order");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading sections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="overline mb-1">Organization</p>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Sections</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your ensemble sections and instrument groups</p>
        </div>
        <div className="flex gap-2">
          {showAdminControls && hasOrderChanges && (
            <Button onClick={saveOrder} className="bg-green-600 hover:bg-green-700 text-white h-10">
              <Save className="w-4 h-4 mr-2" />
              Save Order
            </Button>
          )}
          {showAdminControls && (
            <Button
              variant="outline"
              size="default"
              onClick={() => { setResetDialogOpen(true); setResetConfirm(""); }}
              className="h-10 border-destructive/30 text-destructive hover:bg-destructive/10"
              data-testid="reset-sections-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Default
            </Button>
          )}
          {showAdminControls && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingSection(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground btn-glow h-10" data-testid="add-section-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Section
                </Button>
              </DialogTrigger>
              <DialogContent className="glass border-white/[0.06] max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingSection ? "Edit Section" : "Add New Section"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Section Name</Label>
                    <Input
                      data-testid="section-name-input"
                      placeholder="e.g., Solo Cornet, Violin 1, Alto Sax"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-secondary/50 border-white/[0.06] h-10"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-primary text-primary-foreground h-10">
                    {editingSection ? "Update Section" : "Create Section"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Music2 className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-sm text-muted-foreground">Total Sections</p>
            <p className="text-2xl font-heading font-bold">{sections.length}</p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-sm text-muted-foreground">Filled Sections</p>
            <p className="text-2xl font-heading font-bold">
              {sections.filter(s => getPlayersInSection(s.id).length > 0).length}
            </p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Music2 className="w-8 h-8 mx-auto text-orange-500 mb-2" />
            <p className="text-sm text-muted-foreground">Empty Sections</p>
            <p className="text-2xl font-heading font-bold">
              {sections.filter(s => getPlayersInSection(s.id).length === 0).length}
            </p>
          </CardContent>
        </Card>
        <Card className="glass">
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-blue-500 mb-2" />
            <p className="text-sm text-muted-foreground">Total Players</p>
            <p className="text-2xl font-heading font-bold">{players.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Instructions for admins */}
      {showAdminControls && (
        <Card className="glass border-primary/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              <strong className="text-primary">Tip:</strong> Use the arrow buttons to reorder sections, then click "Save Order". 
              Add new sections with the button above, or edit/delete existing ones.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Sections List */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Music2 className="w-5 h-5 text-primary" />
            All Sections ({sections.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sections.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Music2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No sections created yet</p>
              {showAdminControls && <p className="text-sm mt-2">Click "Add Section" to get started</p>}
            </div>
          ) : (
            <div className="space-y-2">
              {sections.map((section, index) => {
                const playersInSection = getPlayersInSection(section.id);
                return (
                  <Collapsible key={section.id} className="rounded-lg bg-secondary/30 overflow-hidden">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 p-3 hover:bg-secondary/50 transition-colors group">
                      {/* Order controls */}
                      {showAdminControls && (
                        <div className="flex flex-col gap-0.5 order-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); moveSection(index, "up"); }}
                            disabled={index === 0}
                            className="p-1 rounded hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move up"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); moveSection(index, "down"); }}
                            disabled={index === sections.length - 1}
                            className="p-1 rounded hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Move down"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {/* Order number */}
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium text-primary order-2 shrink-0">
                        {index + 1}
                      </div>
                      
                      {/* Expand trigger and section name — grows to take row width */}
                      <CollapsibleTrigger className="flex-1 min-w-[140px] flex items-center gap-2 text-left order-3">
                        <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform duration-200 [[data-state=open]_&]:rotate-90 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{section.name}</p>
                          {section.parts && section.parts.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`parts-list-${section.id}`}>
                              {section.parts
                                .slice()
                                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                                .map((part) => (
                                  <span
                                    key={part.id || part.name}
                                    className="text-[10px] uppercase tracking-wider text-amber-400/80 border border-amber-400/20 bg-amber-400/[0.05] rounded px-1.5 py-0.5"
                                  >
                                    {part.name}
                                  </span>
                                ))}
                            </div>
                          )}
                        </div>
                      </CollapsibleTrigger>
                      
                      {/* Player count badge */}
                      <Badge 
                        variant={playersInSection.length > 0 ? "default" : "secondary"}
                        className={`order-4 shrink-0 ${playersInSection.length > 0 ? "bg-green-500/20 text-green-400 border-green-500/30" : ""}`}
                      >
                        <Users className="w-3 h-3 mr-1" />
                        {playersInSection.length}
                      </Badge>
                      
                      {/* Edit/Delete buttons — always visible on mobile (no hover on touch); hover-reveal on desktop */}
                      {showAdminControls && (
                        <div className="flex gap-1 order-5 md:order-5 md:opacity-0 md:group-hover:opacity-100 md:transition-opacity basis-full md:basis-auto justify-end md:justify-start md:ml-auto">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); openBulkAdd(section); }}
                            className="h-8 w-8 text-blue-400 hover:text-blue-300"
                            title="Add multiple players to this section"
                            data-testid={`bulk-add-${section.id}`}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleClone(section); }}
                            className="h-8 w-8 text-purple-400 hover:text-purple-300"
                            title="Clone this section (e.g. Solo Cornet 2)"
                            data-testid={`clone-section-${section.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); openPartsDialog(section); }}
                            className="h-8 w-8 text-amber-400 hover:text-amber-300"
                            title="Manage parts (Solo, 1st, 2nd…)"
                            data-testid={`parts-section-${section.id}`}
                          >
                            <ListTree className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleEdit(section); }}
                            className="h-8 w-8"
                            title="Edit section"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); confirmDelete(section); }}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            title="Delete section"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Expandable player list */}
                    <CollapsibleContent>
                      <div className="px-4 pb-3 pt-1 border-t border-white/5">
                        {playersInSection.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-2 pl-12">No players assigned</p>
                        ) : (
                          <div className="space-y-1 pl-12">
                            {playersInSection.map((player) => (
                              <div 
                                key={player.id} 
                                className="flex items-center gap-3 py-2 px-3 rounded-md bg-background/30 hover:bg-background/50 transition-colors"
                              >
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <User className="w-4 h-4 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{player.name}</p>
                                  {player.email && (
                                    <p className="text-xs text-muted-foreground truncate">{player.email}</p>
                                  )}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {player.player_type === "member" ? "Member" : player.player_type}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk-Add Players Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="glass border-white/10 max-w-2xl" data-testid="bulk-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-400" />
              Add players to {bulkSection?.name || ""}
            </DialogTitle>
            <DialogDescription>
              Add one row per player. A welcome email with a temporary password is sent to each new email. Rows already in the band are skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2 pr-1">
            {bulkRows.map((row, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_110px_auto] gap-2 items-center" data-testid={`bulk-row-${idx}`}>
                <Input
                  placeholder="Full name"
                  value={row.name}
                  onChange={(e) => updateBulkRow(idx, "name", e.target.value)}
                  data-testid={`bulk-name-${idx}`}
                />
                <Input
                  placeholder="email@example.com"
                  type="email"
                  value={row.email}
                  onChange={(e) => updateBulkRow(idx, "email", e.target.value)}
                  data-testid={`bulk-email-${idx}`}
                />
                <Input
                  placeholder="Phone"
                  value={row.phone}
                  onChange={(e) => updateBulkRow(idx, "phone", e.target.value)}
                  data-testid={`bulk-phone-${idx}`}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  disabled={bulkRows.length === 1}
                  onClick={() => removeBulkRow(idx)}
                  data-testid={`bulk-remove-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={addBulkRow}
            className="w-full border-dashed"
            data-testid="bulk-add-row-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Another row
          </Button>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitBulkAdd}
              disabled={bulkSubmitting}
              className="bg-primary text-primary-foreground"
              data-testid="bulk-submit-btn"
            >
              {bulkSubmitting ? "Adding…" : "Add players"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Parts Dialog */}
      <Dialog open={partsDialogOpen} onOpenChange={(open) => {
        setPartsDialogOpen(open);
        if (!open) { setPartsSection(null); setPartsDraft([]); }
      }}>
        <DialogContent className="glass border-white/10 max-w-md" data-testid="parts-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <ListTree className="w-5 h-5 text-amber-400" />
              Manage Parts — {partsSection?.name}
            </DialogTitle>
            <DialogDescription>
              Define the parts (e.g. Solo, 1st, 2nd) that players in this section can be assigned to. You can edit, reorder or remove them at any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[360px] overflow-y-auto">
            {partsDraft.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-6">No parts defined yet — add one below.</p>
            )}
            {partsDraft.map((part, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`parts-row-${idx}`}>
                <span className="w-6 text-xs text-muted-foreground tabular-nums text-right">{idx + 1}.</span>
                <Input
                  value={part.name}
                  placeholder={["Solo", "1st", "2nd"][idx] || "Part name"}
                  onChange={(e) => updatePartRow(idx, e.target.value)}
                  data-testid={`parts-row-input-${idx}`}
                  className="flex-1"
                />
                <Button variant="ghost" size="icon" onClick={() => movePartRow(idx, -1)} disabled={idx === 0} className="h-8 w-8">
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => movePartRow(idx, 1)} disabled={idx === partsDraft.length - 1} className="h-8 w-8">
                  <ChevronDown className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => removePartRow(idx)} className="h-8 w-8 text-destructive">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addPartRow} data-testid="parts-add-row">
            <Plus className="w-4 h-4 mr-1" /> Add a part
          </Button>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPartsDialogOpen(false)} disabled={partsSaving}>Cancel</Button>
            <Button onClick={savePartsList} disabled={partsSaving} data-testid="parts-save-btn">
              {partsSaving ? "Saving…" : "Save parts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset to Default Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={(open) => {
        setResetDialogOpen(open);
        if (!open) setResetConfirm("");
      }}>
        <DialogContent className="glass border-destructive/30 max-w-md" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Reset all sections to default
            </DialogTitle>
            <DialogDescription>
              This will permanently delete every section in your band and reseed the defaults for your band type.
              Player profiles are preserved, but their section and part assignments will be cleared — you'll need to reassign them.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm">
            Type <strong>RESET</strong> to confirm.
          </div>
          <Input
            value={resetConfirm}
            onChange={(e) => setResetConfirm(e.target.value)}
            placeholder="RESET"
            data-testid="reset-confirm-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} disabled={resetting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={performReset}
              disabled={resetting || resetConfirm.trim() !== "RESET"}
              data-testid="reset-confirm-btn"
            >
              {resetting ? "Resetting…" : "Reset sections"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="glass border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Section
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{sectionToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          {sectionToDelete && getPlayersInSection(sectionToDelete.id).length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-sm text-orange-400">
              <strong>Warning:</strong> This section has {getPlayersInSection(sectionToDelete.id).length} player(s) assigned. 
              You must reassign them before deleting.
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={sectionToDelete && getPlayersInSection(sectionToDelete.id).length > 0}
            >
              Delete Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sections;
